import React, { useEffect, useState } from "react";
import { Box, Typography, CircularProgress, Button, TextField } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import styled from "@emotion/styled";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Api from "../../api/Api";
import { Urls } from "../../api/Urls";
import { useAuthContext } from "../../hooks/useAuthContext";
import ContentBox from "../../components/ui/ContentBox";
import ButtonSquare from "../../components/ui/ButtonSquare";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import { useNavigate } from "react-router-dom";

function MakeupRequests() {
  const { user } = useAuthContext();
  const access_token = user?.access_token;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [data, setData] = useState({});

  useEffect(() => {
    document.title = "BTA admin - Makeup Requests";
    fetchRequests();
  }, [search]);

  const fetchRequests = () => {
    setLoading(true);
    Api.get(Urls.GET_MAKEUP_REQUESTS, {
      headers: { "content-type": "application/json", authorization: `Bearer ${access_token}` },
      params: {
        perPage: 10,
        ...(search && { search: search }),
      },
    })
      .then((res) => {
        setRequests(res?.data?.data || res?.data?.data?.data || []);
        setData(res?.data);
        setLoading(false);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Failed to fetch requests");
        setLoading(false);
      });
  };

  const handleDelete = (id) => {
    // TODO: call API to delete the request, then refresh
    toast.info("Suppression à implémenter côté backend.");
  };

  const handleViewDetails = (request) => {
    navigate(`/admin/makeup-requests/${request.id}`, { state: { request } });
  };

  // Styled components pour la table
  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: "white",
      paddingTop: "8px",
      paddingBottom: "8px",
      letterSpacing: "1px",
      color: `#4B465C`,
      fontSize: 13,
      fontWeight: 500,
    },
    [`&.${tableCellClasses.body}`]: {
      background: "white",
      fontSize: 14,
      color: `#666D83`,
      fontWeight: "500",
    },
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:last-child td, &:last-child th": {
      border: 0,
    },
    ":hover": {
      filter: "brightness(0.95)",
    },
    cursor: "pointer",
  }));

  const StyledTable = styled(Table)({
    borderRadius: "0px",
  });

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return '#FFA500';
      case 'approved':
      case 'manual_approved':
        return '#4CAF50';
      case 'rejected':
        return '#F44336';
      case 'refunded':
        return '#9C27B0';
      default:
        return '#666D83';
    }
  };

  const formatSession = (session) => {
    if (!session) return "-";
    return (
      <Box>
        <Typography fontWeight="500" fontSize="14px">
          {session.date_time ? moment(session.date_time).format("MMM DD, YYYY, HH:mm") : "-"}
        </Typography>
        <Typography fontSize="12px" color="#666D83">
          {session.type ? session.type : "-"}
        </Typography>
      </Box>
    );
  };

  return (
    <Box display="flex" flexDirection="column" gap="26px" marginTop="26px">
      <ToastContainer />
      <Box>
        <Typography fontSize="22px" fontWeight="500" lineHeight="30px" color="#4B465C">
          Makeup Requests
        </Typography>
      </Box>
      <ContentBox>
        <Box display="flex" flexDirection="column" gap="16px">
          <Box>
            <Typography fontSize="18px" fontWeight="500" lineHeight="24px" color="#4B465C">
              List of makeup requests
            </Typography>
          </Box>
          <Box justifyContent="space-between" marginTop="8px" display="flex" gap="16px" flexWrap="wrap" flexDirection="row" alignItems="center">
            <Box display="flex" alignItems="center" gap="16px" flexWrap="wrap">
              <TextField 
                value={search} 
                onChange={handleSearchChange} 
                type="input" 
                placeholder="Search requests..." 
              />
            </Box>
          </Box>
          {loading && (
            <Box display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          )}
          {!loading && (
            <TableContainer sx={{ borderRadius: "0px", border: `1px solid #DBDADE` }}>
              <StyledTable aria-label="customized table">
                <TableHead>
                  <TableRow>
                    <StyledTableCell width="15%">USER</StyledTableCell>
                    <StyledTableCell width="15%">PLAYER</StyledTableCell>
                    <StyledTableCell width="20%" align="left">CURRENT SESSION</StyledTableCell>
                    <StyledTableCell width="20%" align="left">NEW SESSION</StyledTableCell>
                    <StyledTableCell width="12%" align="center">STATUS</StyledTableCell>
                    <StyledTableCell width="10%" align="left">CREATED</StyledTableCell>
                    <StyledTableCell width="8%" align="center">ACTIONS</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requests?.map((req) => (
                    <StyledTableRow key={req.id} onClick={() => handleViewDetails(req)} style={{ cursor: 'pointer' }}>
                      <StyledTableCell align="left">
                        <Box>
                          <Typography fontWeight="500" fontSize="14px">
                            {req.user?.first_name} {req.user?.last_name}
                          </Typography>
                          <Typography fontSize="12px" color="#666D83">
                            {req.user?.email}
                          </Typography>
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell align="left">
                        <Typography fontWeight="500" fontSize="14px">
                          {req.player?.first_name} {req.player?.last_name}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell align="left">
                        {formatSession(req.current_session)}
                      </StyledTableCell>
                      <StyledTableCell align="left">
                        {req.selected_slot ? formatSession(req.selected_slot) : (
                          <Typography fontSize="12px" color="#FFA500">
                            -
                          </Typography>
                        )}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Box 
                          sx={{ 
                            backgroundColor: getStatusColor(req.status),
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '500',
                            textTransform: 'capitalize'
                          }}
                        >
                          {req.status_label}
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell align="left">
                        <Typography fontSize="14px">
                          {req.created_at ? moment(req.created_at).format("MMM DD, YYYY") : "-"}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell align="center" onClick={e => e.stopPropagation()}>
                        <Box justifyContent="center" display="flex" gap="4px">
                          <ButtonSquare 
                            onClick={() => handleViewDetails(req)} 
                            action="show_outlined" 
                          />
                          <ButtonSquare 
                            onClick={() => handleDelete(req.id)} 
                            action="delete_red" 
                            disabled={actionLoading}
                          />
                        </Box>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </StyledTable>
            </TableContainer>
          )}
          {requests?.length === 0 && !loading && (
            <Typography textAlign="center">No makeup requests found</Typography>
          )}
        </Box>
      </ContentBox>
    </Box>
  );
}

export default MakeupRequests; 